import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { ItemProvider, useItemProviderContext } from '@common/react/components/Core/ItemProvider/ItemProvider';
import ServerPageProvider from '@common/react/components/UI/lang/SeverPageProviderWithLang/ServerpageProviderWithLang';
import ExternalHtml from '@common/react/components/UI/ExternalHtml/ExternalHtml';
import BaseModalContextProvider from '@common/react/components/base/BaseModalContextProvider/BaseModalContextProvider';
import { List } from '@common/typescript/objects/List';

import { NTBaseItemPage, Page } from '@commonTuna/react/objects/Page';
import { FaqPage } from '@commonTuna/react/objects/FaqPage';
import { Review } from '@commonTuna/react/objects/Review';

import { getPageInfo, getPageShortName, getBackgroundUrl } from '@app/components/Utils';
import FooterInquiryForm from '@app/components/UI/Footer/FooterInquiry';
import ShareLinks from '@app/components/UI/ShareLinks/ShareLinks';
import Cases from '@app/components/Pages/Section/Cases';
import FAQ from '@app/components/UI/FAQ/FAQ';
import Reviews from '@app/components/Pages/Home/Reviews';

import '@app/scss/pages/section.scss';
import '@app/scss/pages/beforeafter.scss';
import '@app/scss/swiper.scss';

interface SectionProps {
	page?: ServicePage;
}

interface ServicePage extends NTBaseItemPage<Page> {
	randomReviews: Array<Review>
}

const SectionInner: React.FC = () => {
	const { state: { item } } = useItemProviderContext<ServicePage>();

	const info = getPageInfo(item);
	const name = getPageShortName(item.item);
	const cases = item.item.cases || [];
	const reviews = item.item?.randomReviews ? {
		list: item.item.randomReviews ?? [],
		count: 0,
		execution: 0,
		offset: 0,
	} : null;
	const faqs = (item.item?.faqs as any as List<FaqPage>)?.list?.map((faqPage) => faqPage.faq) || [];
	const fileSrc = item.item.originalAvatar ? `/remote/${item.item.originalAvatar}` : '';

	return <main className="section-page">
		<div className="inner-page page-with-special">
			<div className="container">
				<div className="inner-page__headline text-center">
					<h1>{name}</h1>
				</div>
				<div className="article">
					<div className="clearfix">
						{fileSrc && <div className="article__image__container pull-left">
							<div
								className="article__image"
								style={{ backgroundImage: `${getBackgroundUrl(fileSrc)}` }}
							/>
						</div>}
						<ExternalHtml html={info ? info.body : ''} />
					</div>
					<div className="text-center mt10">
						<div className="line" />
						<ShareLinks />
					</div>
				</div>
				{faqs?.length > 0 && <div>
					<div className="extra-text-block" style={{ margin: 0, paddingBottom: 0 }}>
						<h2>
							FAQ
						</h2>
						<h3 className="text-center">
							Frequently asked questions
							{' '}
							<span className="separator">-</span>
							{' '}
							Everything you need to know
						</h3>
					</div>
					<FAQ faqs={faqs} />
				</div>}
				{reviews?.list?.length ? <>
					<div className="extra-text-block" style={{ margin: 0, paddingBottom: 0 }}>
						<h2>
							Reviews
						</h2>
					</div>
					<Reviews reviews={reviews} />
				</> : null}
			</div>
			<BaseModalContextProvider>
				<Cases cases={cases.map((item) => item.case)} pageId={(item as any).item.id} />
			</BaseModalContextProvider>
			<FooterInquiryForm />
		</div>
	</main>;
};

const Section: React.FC<SectionProps> = ({
	page,
}) => {
	const location = useLocation();
	const { actions: { load } } = useItemProviderContext<ServicePage>();
	const [skipLoad, setSkipLoad] = React.useState(true);

	React.useEffect(() => {
		const newPath = `/${page?.fullPath === 'home' ? '' : page?.fullPath}`;
		if (!skipLoad && newPath !== location.pathname) {
			load();
		}
		setSkipLoad(false);
	}, [location.pathname]);

	return (
		<ItemProvider<ServicePage>
			id={page?.id || 0}
			key={page?.id || 0}
			additionalParams={{ fullPath: location.pathname, withPages: true, withChildren: true }}
			loadRequest="getPageRemote"
			item={page}
		>
			<SectionInner />
		</ItemProvider>
	);
};

const SectionWrapper: React.FC = () => {
	return <ServerPageProvider<ServicePage>
		inner={(serverPage) => <Section page={serverPage} />}
	/>;
};

export default SectionWrapper;
